/**
 * Dépendances du traitement d'un lien reçu par l'application native (schéma personnalisé ou lien universel)
 */
export interface UrlOpenDeps<C extends { url: string }> {
  /** Se résout quand les données de l'application (dont les centres) sont chargées */
  ready: () => Promise<void>;
  centers: () => readonly C[];
  selectCenter: (center: C) => void;
  navigate: (url: string) => Promise<unknown>;
  onNavigationError: (err: unknown) => void;
  openBrowser: (url: string) => void;
  closeBrowser: () => void;
}

/**
 * Traite un lien reçu par l'application native.
 * Au lancement à froid, le lien arrive avant le chargement des données : sans attendre, la liste des centres est
 * vide, aucun centre ne correspond et l'application reste sur l'accueil.
 * @param url Le lien reçu
 * @param deps Les dépendances (données, navigation, navigateur)
 */
export async function handleUrlOpen<C extends { url: string }>(url: string, deps: UrlOpenDeps<C>): Promise<void> {
  if (url.includes('/cdn/')) {
    deps.openBrowser(url);
    return;
  }
  try {
    await deps.ready();
  } catch (err) {
    // Données indisponibles : on traite quand même ce qui n'en dépend pas
    console.error(err);
  }
  const navigate = (target: string): void => {
    deps.navigate(target).catch(err => deps.onNavigationError(err));
  };
  const authSlug = url.split('.app').slice(1).join('.app');
  if (authSlug) {
    navigate(authSlug);
  }
  for (const center of deps.centers()) {
    if (!url.startsWith(center.url)) {
      continue;
    }
    deps.selectCenter(center);
    navigate(url.substring(center.url.length));
  }
  deps.closeBrowser();
}
